<template>
  <content-subpage
    :visible="visible"
    :titles="titles"
    :has-parent-subpage="hasParentSubpage"
    :back-label="backLabel"
    class="teacher-selection-listing-content-subpage"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <teacher-selection-listing
        :task="task"
        :excluded-user-ids="params.excludedUserIds"
        :select-first-available-school="params.selectFirstAvailableSchool"
        :selected-school-code="params.selectedSchoolCode"
        :disable-filter="params.disableFilter"
        :level-and-courses="params.levelAndCourses"
        :fetch-schools="params.fetchSchools"
        :fetch-teachers="params.fetchTeachers"
        @executed="execution = $event"
      />
    </template>
    <template #action-button>
      <cv-button class="add-selected-btn" :disabled="isDisabled" @click="handleAddTeacher">
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("add_teachers_button_label") }}</span>
      </cv-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import TeacherSelectionListing from "@plugins/teacher-selection-listing/components/TeacherSelectionListing.vue";
import { ref, computed, watch, onMounted } from "vue";
import { useContentSubpage } from "@/plugins/content-subpage/composables/content-subpage";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["onTeacherSelected"]);

// Data
const task = ref("");
const execution = ref({});
const parentSubpageName = ref("");

// Props
interface Props {
  params: object;
}
const props = defineProps<Props>();

// Composable
const dirtyId = ref("TeacherSelection");
const {
  visible,
  hasParentSubpage,
  titles,
  actionBack,
  actionHidden,
  isComponentDirty,
  removeDirty,
  defaultExpose,
} = useContentSubpage(props.params, dirtyId);
const { t } = useI18n();

// Computed
const isDisabled = computed(() => {
  return !isComponentDirty();
});

const backLabel = computed(() => {
  return `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`;
});

// Methods
const handleAddTeacher = function () {
  task.value = "getSelectedTeacher";
};

// Watch
watch(execution, function () {
  if (task.value === "getSelectedTeacher") {
    if (execution.value.errors.length > 0) {
      task.value = "";
      return;
    }
    emit("onTeacherSelected", execution.value.data);
    removeDirty(dirtyId.value);
    actionBack();
  }
});

// Created
onMounted(() => {
  if (hasParentSubpage) {
    parentSubpageName.value = titles.value.slice(-1)[0];
  }
  if (!props.params.hideTitle) {
    titles.value.push(t("add_teachers_header_label"));
  }
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";

.teacher-selection-listing-content-subpage {
  .cv-search {
    .bx--search {
      background-color: $ui-background;
      border-radius: 10px;
      .bx--search-close {
        height: 100%;
      }
    }
    .bx--search-input,
    .filter-selection {
      background-color: inherit;
      &:hover {
        background-color: $hover-ui;
      }
    }
  }
}
</style>
